import styled, { keyframes } from 'styled-components';

import RefreshIconSvg from '../../../assets/icons/icon-home-dashboard-refresh.svg?react';
import bonusGiftImage from '../../../assets/images/home-dashboard-bonus-gift.png';
import { BaseButton, UserAvatar } from '../../../shared/ui';

type HomeDashboardHeaderRowProps = {
  avatarUrl?: string | null;
  isRefreshing?: boolean;
  onOpenProfile?: () => void;
  onOpenReferral?: () => void;
  onRefresh?: () => void;
  profileName: string;
  referralBonus: string;
  refreshAriaLabel: string;
  refreshDisabled?: boolean;
};

const refreshIconSpin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const HeaderRow = styled.div.attrs({
  'data-ui': 'home-dashboard-header-row',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const ProfileCluster = styled(BaseButton).attrs({
  'data-ui': 'home-dashboard-profile-button',
})`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(10)};
`;

const ProfileName = styled.p`
  min-width: 0;
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(14)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RightActions = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const BonusBadge = styled(BaseButton).attrs({
  'data-ui': 'home-dashboard-referral-badge-button',
})`
  height: ${({ theme }) => theme.scale.px(42)};
  border-radius: ${({ theme }) => theme.scale.px(7)};
  padding: 0 ${({ theme }) => theme.scale.px(10)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(6)};
  background: ${({ theme }) => theme.colors.brand.emphasis};
`;

// Intentionally uses a dedicated bitmap illustration asset for the header badge.
// This is not the same functional icon as `GiftIconSvg` from summary cards.
const GiftIcon = styled.img.attrs({
  src: bonusGiftImage,
  alt: '',
  'aria-hidden': true,
})`
  flex: 0 0 auto;
  width: ${({ theme }) => theme.scale.px(25)};
  height: ${({ theme }) => theme.scale.px(25)};
  display: block;
`;

const BonusText = styled.p`
  width: ${({ theme }) => theme.scale.px(57)};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(14)};
  white-space: pre-line;
`;

const RefreshButton = styled(BaseButton).attrs({
  'data-ui': 'home-dashboard-refresh-button',
})`
  width: ${({ theme }) => theme.scale.px(42)};
  height: ${({ theme }) => theme.scale.px(42)};
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.brand.emphasis};
`;

const RefreshIcon = styled(RefreshIconSvg).attrs({
  'aria-hidden': true,
})<{ $isRefreshing: boolean }>`
  width: ${({ theme }) => theme.scale.px(21)};
  height: ${({ theme }) => theme.scale.px(21)};
  display: block;
  animation: ${({ $isRefreshing }) =>
      $isRefreshing ? refreshIconSpin : 'none'}
    760ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export function HomeDashboardHeaderRow({
  avatarUrl = null,
  isRefreshing = false,
  onOpenProfile,
  onOpenReferral,
  onRefresh,
  profileName,
  referralBonus,
  refreshAriaLabel,
  refreshDisabled = false,
}: HomeDashboardHeaderRowProps) {
  const referralAriaLabel = referralBonus.replace(/\s+/g, ' ');

  return (
    <HeaderRow>
      <ProfileCluster type="button" onClick={onOpenProfile}>
        <UserAvatar avatarUrl={avatarUrl} sizePx={42} />
        <ProfileName>{profileName}</ProfileName>
      </ProfileCluster>
      <RightActions>
        <BonusBadge
          type="button"
          aria-label={referralAriaLabel}
          onClick={onOpenReferral}
        >
          <GiftIcon />
          <BonusText>{referralBonus}</BonusText>
        </BonusBadge>
        <RefreshButton
          type="button"
          aria-busy={isRefreshing}
          aria-label={refreshAriaLabel}
          disabled={refreshDisabled}
          onClick={onRefresh}
        >
          <RefreshIcon $isRefreshing={isRefreshing} />
        </RefreshButton>
      </RightActions>
    </HeaderRow>
  );
}
